import { selectUserId } from "modules/auth/redux/selectors";
import useLobbyGuestFirestore from "modules/lobby/hooks/useLobbyGuestFirestore";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import {
  selectCurrentEventId,
  selectCurrentEventIsLobbyActive,
  selectIsEventManager,
  selectIsEventSpeaker,
} from "../../selectors";
import { EventPrechecks, PrecheckStatus } from "../constants";
import { PrecheckResult } from "../types";

export const useLobbyCheck = (): PrecheckResult => {
  const lobbyActive = useSelector(selectCurrentEventIsLobbyActive);
  const userId = useSelector(selectUserId);
  const eventId = useSelector(selectCurrentEventId);
  const {
    location: { pathname },
  } = useHistory();
  const { isGuestApprovedIntoEvent } = useLobbyGuestFirestore();
  const isEventManager = useSelector(selectIsEventManager);
  const isEventSpeaker = useSelector(selectIsEventSpeaker);

  const {
    data: isGuestApproved,
    status: requestStatus,
    isLoading,
  } = useQuery({
    queryKey: ["lobbyApproval", eventId, userId, pathname],
    queryFn: () => {
      if (
        !eventId ||
        !userId ||
        !lobbyActive ||
        isEventManager ||
        isEventSpeaker
      ) {
        return undefined;
      }
      return isGuestApprovedIntoEvent(eventId, userId);
    },
    gcTime: 0,
  });

  const status = useMemo(() => {
    if (!eventId) {
      // catch cases where !lobbyActive isn't a reflection of lobby being off, just event data not loaded yet
      return PrecheckStatus.FAIL;
    }
    if (!lobbyActive || isEventManager || isEventSpeaker) {
      return PrecheckStatus.PASS;
    }
    if (requestStatus === "success") {
      return isGuestApproved ? PrecheckStatus.PASS : PrecheckStatus.FAIL;
    }
    if (isLoading) {
      return PrecheckStatus.PENDING;
    }

    return PrecheckStatus.FAIL;
  }, [
    eventId,
    isGuestApproved,
    requestStatus,
    isLoading,
    lobbyActive,
    isEventManager,
    isEventSpeaker,
  ]);

  return { name: EventPrechecks.LOBBY, status };
};
